import React from 'react';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import HealingOutlinedIcon from '@mui/icons-material/HealingOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { isCbcFaculty } from '../../utils/facultyRole';
import {
  FACULTY_DIARY_PATH,
  FACULTY_LESSON_PLANS_PATH,
  FACULTY_STUDENTS_PATH,
  FACULTY_INTERVENTIONS_PATH,
  FACULTY_ACTIVITY_FEED_PATH,
  FACULTY_TEACHING_REPORTS_PATH,
  FACULTY_LAB_REVIEWS_PATH,
  FACULTY_PROJECT_REVIEWS_PATH,
} from '../../constants/facultyNav';
import { FACULTY_PROBLEM_LIBRARY_PATH, FACULTY_PROBLEM_LIBRARY_ANALYTICS_PATH } from '../../constants/problemLibrary';
import { FACULTY_ASSESSMENTS_PATH } from '../../constants/facultyAssessments';
import { ADMIN_PROBLEM_LIBRARY_PATH, ADMIN_PROBLEM_LIBRARY_ANALYTICS_PATH } from '../../constants/problemLibrary';
import { ADMIN_PROBLEM_INTELLIGENCE_OPS_PATH } from '../../constants/problemIntelligenceOps';
import { isProblemIntelligenceReadUiEnabled } from '../../config/problemIntelligenceFlags';
import { isAssessmentOrchestrationUiEnabled } from '../../config/assessmentFlags';
import { isReportingIntelligenceUiEnabled } from '../../config/reportingIntelligenceFlags';
import { isDiaryTransferUiEnabled } from '../../config/diaryPortabilityFlags';
import { isAdminSyncOpsUiEnabled } from '../../config/adminSyncOpsFlags';
import { RI_BASE_FACULTY } from '../../constants/reportingIntelligence';

/**
 * Icon keys referenced by {@link NAV_CONFIG} items.
 * Add new entries here when introducing new nav icons.
 */
export const NAV_ICON_MAP = {
  spaceDashboard: SpaceDashboardOutlinedIcon,
  analytics: AnalyticsOutlinedIcon,
  policy: PolicyOutlinedIcon,
  dashboard: DashboardOutlinedIcon,
  school: SchoolOutlinedIcon,
  assignment: AssignmentOutlinedIcon,
  tune: TuneOutlinedIcon,
  people: PeopleOutlineOutlinedIcon,
  lock: LockOutlinedIcon,
  eventNote: EventNoteOutlinedIcon,
  menuBook: MenuBookOutlinedIcon,
  quiz: QuizOutlinedIcon,
  healing: HealingOutlinedIcon,
  listAlt: ListAltOutlinedIcon,
  grade: GradeOutlinedIcon,
  science: ScienceOutlinedIcon,
  folder: FolderOutlinedIcon,
  dynamicFeed: DynamicFeedOutlinedIcon,
  assessment: AssessmentOutlinedIcon,
  profile: PersonOutlineIcon,
  accountTree: AccountTreeOutlinedIcon,
  build: BuildOutlinedIcon,
  backup: BackupOutlinedIcon,
  gavel: GavelOutlinedIcon,
  hub: HubOutlinedIcon,
  toggleOn: ToggleOnOutlinedIcon,
  swapHoriz: SwapHorizOutlinedIcon,
};

/**
 * Visibility enum constants
 */
export const VISIBILITY_ENUM = {
  FACULTY_ONLY: 'facultyOnly',
  CBC_ONLY: 'cbcOnly',
  STUDENT_ONLY: 'studentOnly',
  ADMIN_ONLY: 'adminOnly',
  STUDENT_PROJECTS: 'studentProjects',
  PROBLEM_INTELLIGENCE: 'problemIntelligence',
  ASSESSMENT_ORCHESTRATION: 'assessmentOrch',
  REPORTING_INTELLIGENCE: 'reportingIntel',
  ADMIN_PROBLEM_INTELLIGENCE: 'adminProblemIntel',
  ADMIN_REPORTING_INTELLIGENCE: 'adminReportingIntel',
  ADMIN_DIARY_TRANSFER: 'adminDiaryTransfer',
  ADMIN_SYNC_OPS: 'adminSyncOps',
};

/**
 * Visibility keys → predicate(authContext). Omitted on an item = always visible when section is shown.
 */
const VISIBILITY = {
  /** Teaching faculty only (not CBC coding role). */
  [VISIBILITY_ENUM.FACULTY_ONLY]: ({ user }) => user?.role === 'faculty',
  [VISIBILITY_ENUM.CBC_ONLY]: ({ user }) => isCbcFaculty(user),
  [VISIBILITY_ENUM.STUDENT_ONLY]: ({ isStudent }) => isStudent,
  [VISIBILITY_ENUM.ADMIN_ONLY]: ({ isAdmin }) => isAdmin,
  /** Student with eligibility for projects feature. */
  [VISIBILITY_ENUM.STUDENT_PROJECTS]: ({ isStudent, eligibility }) => isStudent && eligibility?.projects,
  /** Faculty with Problem Intelligence UI enabled. */
  [VISIBILITY_ENUM.PROBLEM_INTELLIGENCE]: ({ user }) => isCbcFaculty(user) && isProblemIntelligenceReadUiEnabled(),
  /** Faculty with Assessment Orchestration enabled. */
  [VISIBILITY_ENUM.ASSESSMENT_ORCHESTRATION]: ({ user }) => user?.role === 'faculty' && isAssessmentOrchestrationUiEnabled(),
  /** Faculty with Reporting Intelligence enabled. */
  [VISIBILITY_ENUM.REPORTING_INTELLIGENCE]: ({ user }) => user?.role === 'faculty' && isReportingIntelligenceUiEnabled(),
  /** Admin with Problem Intelligence UI enabled. */
  [VISIBILITY_ENUM.ADMIN_PROBLEM_INTELLIGENCE]: ({ user }) => user?.role === 'admin' && isProblemIntelligenceReadUiEnabled(),
  /** Admin with Reporting Intelligence enabled. */
  [VISIBILITY_ENUM.ADMIN_REPORTING_INTELLIGENCE]: ({ user }) => user?.role === 'admin' && isReportingIntelligenceUiEnabled(),
  /** Admin with Diary Transfer UI enabled. */
  [VISIBILITY_ENUM.ADMIN_DIARY_TRANSFER]: ({ user }) => user?.role === 'admin' && isDiaryTransferUiEnabled(),
  /** Admin with Sync Operations UI enabled. */
  [VISIBILITY_ENUM.ADMIN_SYNC_OPS]: ({ user }) => user?.role === 'admin' && isAdminSyncOpsUiEnabled(),
};

/**
 * Single source of truth for sidebar structure.
 * Sections with no visible items after filtering are omitted.
 *
 * @typedef {Object} NavConfigItem
 * @property {string} id
 * @property {string} label
 * @property {string} path
 * @property {keyof NAV_ICON_MAP} icon
 * @property {string} [matchPath] - optional override for active-route matching (e.g. progress)
 * @property {keyof VISIBILITY} [visibility]
 *
 * @typedef {Object} NavConfigSection
 * @property {string} id
 * @property {string} title
 * @property {NavConfigItem[]} items
 */

export const NAV_CONFIG = [
  // ========== STUDENT NAVIGATION (Primary) ==========
  {
    id: 'student-dashboard',
    title: null,
    collapsible: false,
    items: [
      {
        id: 'student-dashboard-link',
        label: 'Dashboard',
        path: '/student/dashboard',
        icon: 'dashboard',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },
    ],
  },
  {
    id: 'student-learning',
    title: 'My Learning',
    items: [
      {
        id: 'student-courses',
        label: 'My courses',
        path: '/student/courses',
        icon: 'school',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },
      {
        id: 'student-assignments',
        label: 'Assignments',
        path: '/student/assignments',
        icon: 'assignment',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },
      {
        id: 'student-assessments',
        label: 'Assessments',
        path: '/student/assessments',
        icon: 'quiz',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },
      {
        id: 'student-progress',
        label: 'My Progress',
        path: '/student/progress',
        icon: 'analytics',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },
      {
        id: 'student-projects',
        label: 'My Projects',
        path: '/student/projects',
        icon: 'folder',
        visibility: VISIBILITY_ENUM.STUDENT_PROJECTS,
      },
    ],
  },
  {
    id: 'student-profile',
    title: null,
    collapsible: false,
    items: [
      {
        id: 'student-profile-link',
        label: 'Profile',
        path: '/student/profile',
        icon: 'profile',
        visibility: VISIBILITY_ENUM.STUDENT_ONLY,
      },

      
    ],
  },
  // ========== CODING WORKSPACE NAVIGATION ==========
  {
    id: 'coding-workspace',
    title: 'Coding Workspace',
    items: [
      {
        id: 'cw-dashboard-admin',
        label: 'Batches',
        path: '/admin/coding-tracker',
        icon: 'spaceDashboard',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'cw-dashboard-cbc',
        label: 'Batches',
        path: '/faculty/workspace/batches',
        icon: 'spaceDashboard',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'cw-plagiarism-admin',
        label: 'Plagiarism Insights',
        path: '/admin/plagiarism-analytics',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'cw-plagiarism-cbc',
        label: 'Plagiarism Insights',
        path: '/faculty/plagiarism-analytics',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'cw-overview-cbc',
        label: 'Coding Overview',
        path: '/faculty/workspace/overview',
        icon: 'spaceDashboard',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'cw-assignments-cbc',
        label: 'Assignment Management',
        path: '/faculty/workspace/assignments',
        icon: 'assignment',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'cw-problem-library-faculty',
        label: 'Problem Library',
        path: FACULTY_PROBLEM_LIBRARY_PATH,
        icon: 'menuBook',
        visibility: VISIBILITY_ENUM.PROBLEM_INTELLIGENCE,
      },
    ],
  },
  {
    id: 'course-management',
    title: 'Course Management',
    items: [
      {
        id: 'cm-admin-overview',
        label: 'Overview',
        path: '/admin/overview',
        icon: 'dashboard',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'cm-faculty-dashboard',
        label: 'Dashboard',
        path: '/faculty/dashboard',
        icon: 'dashboard',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'cm-courses',
        label: 'Courses',
        path: '/faculty/courses',
        icon: 'school',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'cm-faculty-diary',
        label: 'Faculty Diary',
        path: FACULTY_DIARY_PATH,
        icon: 'eventNote',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'cm-lesson-plans',
        label: 'Lesson Plans',
        path: FACULTY_LESSON_PLANS_PATH,
        icon: 'menuBook',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'cm-assessments-faculty',
        label: 'Assessments',
        path: FACULTY_ASSESSMENTS_PATH,
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.ASSESSMENT_ORCHESTRATION,
      },
    ],
  },
  {
    id: 'teaching-work',
    title: 'Teaching Work',
    items: [
      {
        id: 'tw-students',
        label: 'Students',
        path: FACULTY_STUDENTS_PATH,
        icon: 'people',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'tw-interventions',
        label: 'Interventions',
        path: FACULTY_INTERVENTIONS_PATH,
        icon: 'healing',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'tw-activity-feed',
        label: 'Activity Feed',
        path: FACULTY_ACTIVITY_FEED_PATH,
        icon: 'dynamicFeed',
        visibility: VISIBILITY_ENUM.CBC_ONLY,
      },
      {
        id: 'tw-lab-reviews',
        label: 'Labs',
        path: FACULTY_LAB_REVIEWS_PATH,
        icon: 'science',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'tw-project-reviews',
        label: 'Projects',
        path: FACULTY_PROJECT_REVIEWS_PATH,
        icon: 'folder',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
    ],
  },
  {
    id: 'faculty-analytics',
    title: 'Faculty Analytics',
    items: [
      {
        id: 'fa-overall',
        label: 'Overall Analytics',
        path: '/faculty/analytics/overall',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-students',
        label: 'Student Progress',
        path: '/faculty/analytics/students',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-batches',
        label: 'Assignment Analytics',
        path: '/faculty/analytics/batches',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-effectiveness',
        label: 'Faculty Effectiveness',
        path: '/faculty/analytics/effectiveness',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-platforms',
        label: 'Platform Analytics',
        path: '/faculty/analytics/platforms',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-problem-analytics',
        label: 'Problem Analytics',
        path: FACULTY_PROBLEM_LIBRARY_ANALYTICS_PATH,
        icon: 'analytics',
        visibility: VISIBILITY_ENUM.PROBLEM_INTELLIGENCE,
      },
      {
        id: 'fa-teaching-reports',
        label: 'Teaching Reports',
        path: FACULTY_TEACHING_REPORTS_PATH,
        icon: 'listAlt',
        visibility: VISIBILITY_ENUM.FACULTY_ONLY,
      },
      {
        id: 'fa-reports',
        label: 'Reports',
        path: RI_BASE_FACULTY,
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.REPORTING_INTELLIGENCE,
      },
    ],
  },
  // ========== ADMIN NAVIGATION (Complete Feature Parity) ==========
  {
    id: 'admin-organization',
    title: 'Organization',
    items: [
      {
        id: 'admin-org-batches',
        label: 'Departments & batches',
        path: '/admin/coding-tracker',
        icon: 'accountTree',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-org-users',
        label: 'Users (students & faculty)',
        path: '/admin/users',
        icon: 'people',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
    ],
  },
  {
    id: 'admin-academic',
    title: 'Academic oversight',
    items: [
      {
        id: 'admin-analytics',
        label: 'Department analytics',
        path: '/admin/analytics',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-reports',
        label: 'Reports',
        path: '/admin/analytics/reporting',
        icon: 'assessment',
        visibility: VISIBILITY_ENUM.ADMIN_REPORTING_INTELLIGENCE,
      },
      {
        id: 'admin-teaching-reports',
        label: 'Teaching Reports',
        path: '/admin/teaching-reports',
        icon: 'eventNote',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-diary-transfer',
        label: 'Faculty Diary Tools',
        path: '/admin/faculty-diary/transfer',
        icon: 'swapHoriz',
        visibility: VISIBILITY_ENUM.ADMIN_DIARY_TRANSFER,
      },
      {
        id: 'admin-courses',
        label: 'Courses overview',
        path: '/admin/courses-overview',
        icon: 'menuBook',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-lab-activity',
        label: 'Lab activity',
        path: '/admin/lab-activity',
        icon: 'science',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
    ],
  },
  {
    id: 'admin-insights',
    title: 'Coding insights',
    items: [
      {
        id: 'admin-platform-health',
        label: 'Platform health',
        path: '/admin/platform-health',
        icon: 'analytics',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-problem-library',
        label: 'Problem Library',
        path: ADMIN_PROBLEM_LIBRARY_PATH,
        matchPath: ADMIN_PROBLEM_LIBRARY_PATH,
        icon: 'menuBook',
        visibility: VISIBILITY_ENUM.ADMIN_PROBLEM_INTELLIGENCE,
      },
      {
        id: 'admin-problem-analytics',
        label: 'Problem Analytics',
        path: ADMIN_PROBLEM_LIBRARY_ANALYTICS_PATH,
        matchPath: ADMIN_PROBLEM_LIBRARY_ANALYTICS_PATH,
        icon: 'analytics',
        visibility: VISIBILITY_ENUM.ADMIN_PROBLEM_INTELLIGENCE,
      },
      {
        id: 'admin-sync-ops',
        label: 'Sync Operations',
        path: '/admin/sync-operations',
        icon: 'healing',
        visibility: VISIBILITY_ENUM.ADMIN_SYNC_OPS,
      },
      {
        id: 'admin-plagiarism',
        label: 'Plagiarism insights',
        path: '/admin/plagiarism-analytics',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-handle-claims',
        label: 'Platform Handle Claims',
        path: '/admin/platform-handle-claims',
        icon: 'gavel',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
    ],
  },
  {
    id: 'admin-system',
    title: 'System',
    initiallyExpanded: false,
    items: [
      {
        id: 'admin-settings',
        label: 'Settings',
        path: '/admin/coding-platforms',
        icon: 'tune',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-data-integrity',
        label: 'Data integrity',
        path: '/admin/data-integrity',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-recovery',
        label: 'System recovery',
        path: '/admin/student-recovery',
        icon: 'healing',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-recovery-center',
        label: 'Recovery Center',
        path: '/admin/recovery-center',
        icon: 'build',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-problem-intel-ops',
        label: 'Problem Intelligence',
        path: ADMIN_PROBLEM_INTELLIGENCE_OPS_PATH,
        icon: 'hub',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-backups',
        label: 'Backups',
        path: '/admin/backups',
        icon: 'backup',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-features',
        label: 'Feature management',
        path: '/admin/features',
        icon: 'toggleOn',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-monitoring-rules',
        label: 'Monitoring rules',
        path: '/admin/monitoring-rules',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-assessment-anomalies',
        label: 'Assessment anomalies',
        path: '/admin/assessment-anomalies',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-monitoring-alarms',
        label: 'Monitoring alarms',
        path: '/admin/monitoring-alarms',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-monitoring-purge',
        label: 'Monitoring purge',
        path: '/admin/monitoring-purge',
        icon: 'policy',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
      {
        id: 'admin-access',
        label: 'Access control',
        path: '/admin/access-control',
        icon: 'lock',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
    ],
  },
  {
    id: 'admin-profile',
    title: 'Profile',
    items: [
      {
        id: 'admin-profile-link',
        label: 'Profile',
        path: '/admin/profile',
        icon: 'profile',
        visibility: VISIBILITY_ENUM.ADMIN_ONLY,
      },
    ],
  },
];

function itemVisible(item, ctx) {
  if (!item.visibility) return true;
  const fn = VISIBILITY[item.visibility];
  return fn ? fn(ctx) : true;
}

/**
 * Resolves icon keys to elements and filters by auth. Preserves label, path, matchPath, id.
 *
 * @param {typeof NAV_CONFIG} config
 * @param {{ isAdmin: boolean, isCbcFaculty: boolean, isStudent: boolean, user: object }} ctx
 * @returns {Array<{ id: string, title: string, items: Array<{ id: string, label: string, path: string, matchPath?: string, icon: React.ReactNode }> }>}
 */
export function getVisibleNavSections(config, ctx) {
  return config
    .map((section) => {
      const items = section.items
        .filter((item) => itemVisible(item, ctx))
        .map((item) => {
          const IconComponent = NAV_ICON_MAP[item.icon];
          const icon = IconComponent ? (
            <IconComponent fontSize="small" />
          ) : null;
          return {
            id: item.id,
            label: item.label,
            path: item.path,
            ...(item.matchPath != null ? { matchPath: item.matchPath } : {}),
            icon,
          };
        });
      return { id: section.id, title: section.title, items };
    })
    .filter((section) => section.items.length > 0);
}
